/**
 * src/shell/channels/channel-plugin-registry.ts
 *
 * Centralized mapping of UI Channel Routes to Canonical Plugin IDs.
 * Ensures consistent filtering across Sidebar, Header dropdowns, and AppShell routing.
 */

export interface ChannelMetadata {
  readonly id: string;
  readonly nameKey: string;
  readonly defaultName: string;
  readonly pluginId?: string; // Canonical Plugin ID (e.g. 'WarehousePlugin', 'CommercePlugin')
  readonly requiresFiscalJurisdiction?: boolean;
}

export const CHANNEL_PLUGIN_MAPPINGS: readonly ChannelMetadata[] = [
  { id: 'general', nameKey: 'channels.general', defaultName: 'General' },
  { id: 'scrum', nameKey: 'channels.scrum', defaultName: 'Scrum & Standup', pluginId: 'ScrumMasterPlugin' },
  { id: 'knowledge-priming', nameKey: 'channels.knowledgePriming', defaultName: 'Knowledge Priming Console', pluginId: 'LLMWikiPlugin' },
  { id: 'cms-translation', nameKey: 'channels.cmsTranslation', defaultName: 'CMS Translation Studio', pluginId: 'CmsPlugin' },
  { id: 'cms', nameKey: 'channels.cms', defaultName: 'Landing Pages & CMS Studio', pluginId: 'CmsPlugin' },
  { id: 'gallery', nameKey: 'channels.gallery', defaultName: 'Art Gallery & Showroom', pluginId: 'GalleryPlugin' },
  { id: 'commerce', nameKey: 'channels.store', defaultName: 'Store & Shopping', pluginId: 'CommercePlugin' },
  { id: 'pos', nameKey: 'channels.pos', defaultName: 'Point-of-Sale Terminal', pluginId: 'PosPlugin' },
  { id: 'kiosk', nameKey: 'channels.kiosk', defaultName: 'Customer Self-Kiosk', pluginId: 'StorefrontPlugin' },
  { id: 'inventory', nameKey: 'channels.inventory', defaultName: 'Warehouse & Inventory', pluginId: 'WarehousePlugin' },
  { id: 'sales-pipeline', nameKey: 'channels.pipeline', defaultName: 'Sales Pipeline', pluginId: 'CrmPlugin' },
  { id: 'invoicing', nameKey: 'channels.invoicing', defaultName: 'Invoicing Studio', pluginId: 'InvoicingPlugin' },
  { id: 'payroll', nameKey: 'channels.payroll', defaultName: 'Payroll & HR Desk' },
  { id: 'ledger', nameKey: 'channels.ledger', defaultName: 'General Ledger' },
  {
    id: 'authority-item-mappings',
    nameKey: 'channels.authorityMappings',
    defaultName: 'Authority Item Mappings',
    pluginId: 'InvoicingPlugin',
    requiresFiscalJurisdiction: true,
  },
  { id: 'investor-faq', nameKey: 'channels.faq', defaultName: 'Investor FAQ' },
  { id: 'ai-demo', nameKey: 'channels.aiDemo', defaultName: 'AI Demo', pluginId: 'AiAgentPlugin' },
  { id: 'engineering-ops', nameKey: 'channels.engineeringOps', defaultName: 'Engineering Ops' },
  { id: 'lifecycle-test', nameKey: 'channels.lifecycleTest', defaultName: 'Lifecycle Test Room' },
];

/**
 * Checks if a given plugin ID or alias is enabled.
 */
export function isPluginIdEnabled(
  enabledPlugins: Record<string, boolean> | undefined,
  pluginId?: string
): boolean {
  if (!pluginId) return true;
  if (!enabledPlugins) return true;

  // Direct exact match
  if (enabledPlugins[pluginId] === false) return false;

  // Equivalence clusters bridging canonical mod-* IDs, PascalCase plugin keys, and legacy UI tokens
  const PLUGIN_CLUSTERS: readonly (readonly string[])[] = [
    ['GalleryPlugin', 'mod-gallery', 'gallery', 'plugin-gallery'],
    ['WhiteboardPlugin', 'mod-whiteboard', 'whiteboard'],
    ['ActionPlannerPlugin', 'mod-action-planner', 'mod-kanban', 'action_planner', 'kanban'],
    ['ChatPlugin', 'mod-chat', 'mod-mesh-chat', 'chat', 'mesh-chat'],
    ['MediaPlugin', 'mod-media', 'mod-video-embed', 'media', 'video-embed'],
    ['WallPlugin', 'mod-wall', 'mod-sovereign-wall', 'wall', 'sovereign-wall', 'feed', 'drops'],
    ['ScrumMasterPlugin', 'scrum'],
    ['CmsPlugin', 'cms', 'cms-translation'],
    ['LLMWikiPlugin', 'RagKnowledgePlugin', 'llmwiki', 'knowledge-priming'],
    ['CommercePlugin', 'commerce', 'commerce_core', 'store'],
    ['PosPlugin', 'pos', 'commerce_pos'],
    ['StorefrontPlugin', 'kiosk', 'storefront_kiosk'],
    ['WarehousePlugin', 'inventory', 'wms'],
    ['CrmPlugin', 'crm', 'sales-pipeline'],
    ['InvoicingPlugin', 'invoicing', 'fiscal_invoicing', 'authority-item-mappings'],
    ['AiAgentPlugin', 'ai-demo', 'shopper_agent'],
    ['CalculatorPlugin', 'mod-calculator', 'calculator'],
    ['ClockPlugin', 'mod-clock', 'clock'],
    ['DoorReleasePlugin', 'mod-door-release', 'door-release']
  ];

  // If the queried ID belongs to an equivalence cluster, verify all related aliases
  for (const cluster of PLUGIN_CLUSTERS) {
    if (cluster.includes(pluginId)) {
      for (const alias of cluster) {
        if (enabledPlugins[alias] === false) return false;
      }
    }
  }

  return true;
}

/**
 * Checks if a specific channel is enabled given the plugin states and active jurisdiction.
 */
export function isChannelEnabled(
  channelId: string,
  enabledPlugins: Record<string, boolean> | undefined,
  activeJurisdiction = 'GLOBAL'
): boolean {
  const meta = CHANNEL_PLUGIN_MAPPINGS.find((ch) => ch.id === channelId);
  if (!meta) return true; // Default unmapped custom channels are open

  if (meta.pluginId && !isPluginIdEnabled(enabledPlugins, meta.pluginId)) {
    return false;
  }

  if (meta.requiresFiscalJurisdiction) {
    const isEfrisOrMyInvois = activeJurisdiction === 'UG' || activeJurisdiction === 'MY';
    if (!isEfrisOrMyInvois) return false;
  }

  return true;
}

/**
 * Fallback channel selector: if current room is disabled, returns 'general'.
 */
export function getSafeActiveChannel(
  currentRoom: string,
  enabledPlugins: Record<string, boolean> | undefined,
  activeJurisdiction = 'GLOBAL'
): string {
  if (isChannelEnabled(currentRoom, enabledPlugins, activeJurisdiction)) {
    return currentRoom;
  }
  return 'general';
}
